import { useState, useEffect } from 'react';
import { FileText, Plus, CheckSquare, Square, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '@/components/common/FormSection';
import { AmountText } from '@/components/finance-v2/FinanceBadges';
import type { InvoicableItem } from '../types/financeV2.types';
import { financeV2Service } from '../services/financeV2Service';
import { cn } from '@/utils/cn';

import { getErrorMessage } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'

export function FinanceV2InvoiceablePage() {
  const [itemsByClient, setItemsByClient] = useState<Record<string, InvoicableItem[]>>({});
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    discount: 0,
    notes: '',
    payment_instructions: '',
  });

  useEffect(() => {
    loadInvoicableItems();
  }, []);

  const loadInvoicableItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await financeV2Service.invoices.getInvoicableItems();
      setItemsByClient(response.data.byClient);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar itens faturáveis'));
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const selectAllForClient = (clientId: string) => {
    const clientItems = itemsByClient[clientId] || [];
    const newSelected = new Set(selectedItems);
    clientItems.forEach(item => newSelected.add(item.id));
    setSelectedItems(newSelected);
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleCreateInvoice = () => {
    if (selectedItems.size === 0) {
      setError('Selecione pelo menos um item para faturar');
      return;
    }

    // Check if all selected items belong to the same client
    const allItems = Object.values(itemsByClient).flat();
    const selectedItemsData = allItems.filter(item => selectedItems.has(item.id));
    const clients = new Set(selectedItemsData.map(item => item.client_id));

    if (clients.size > 1) {
      setError('Selecione apenas itens do mesmo cliente para criar uma fatura');
      return;
    }

    setShowCreateModal(true);
  };

  const submitCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      // Get all selected items
      const allItems = Object.values(itemsByClient).flat();
      const selectedItemsData = allItems.filter(item => selectedItems.has(item.id));

      if (selectedItemsData.length === 0) {
        throw new Error('Nenhum item selecionado');
      }

      const clientId = selectedItemsData[0].client_id;

      // Convert items to invoice format
      const invoiceItems = selectedItemsData.map(item => ({
        item_type: (item.type === 'timesheet' ? 'TIMESHEET' : 'CLIENT_EXPENSE') as 'FEE' | 'CLIENT_EXPENSE' | 'TIMESHEET',
        transaction_id: item.transaction_id,
        timesheet_id: item.timesheet_id,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total: item.total,
      }));

      await financeV2Service.invoices.create({
        client_id: clientId,
        items: invoiceItems,
        discount: invoiceData.discount,
        due_date: invoiceData.due_date,
        notes: invoiceData.notes || undefined,
        payment_instructions: invoiceData.payment_instructions || undefined,
      });

      setSuccess('Fatura criada com sucesso!');
      setShowCreateModal(false);
      setSelectedItems(new Set());
      loadInvoicableItems();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar fatura'));
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return parseDbTimestamp(dateString)?.toLocaleDateString('pt-BR') ?? '-';
  };

  // Calculate selected totals
  const allItems = Object.values(itemsByClient).flat();
  const selectedItemsData = allItems.filter(item => selectedItems.has(item.id));
  const selectedTotal = selectedItemsData.reduce((sum, item) => sum + item.total, 0);
  const selectedClient = selectedItemsData.length > 0 ? selectedItemsData[0].client_name : null;

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      {/* Header */}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <FileText className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Itens a Faturar</h1>
        </div>
        <p className="text-sm text-muted-foreground">Selecione transações e horas para criar faturas</p>
      </div>

      {/* Messages */}
      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
          {error}
        </div>
      )}

      {success && (
        <div role="status" className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary">
          <CheckSquare className="size-4 shrink-0" aria-hidden="true" />
          {success}
        </div>
      )}

      {/* Selection Summary */}
      {selectedItems.size > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1 text-sm">
            <div className="font-medium">
              {selectedItems.size} {selectedItems.size === 1 ? 'item selecionado' : 'itens selecionados'}
            </div>
            {selectedClient && (
              <div className="break-words text-muted-foreground">
                Cliente: <span className="font-semibold text-foreground">{selectedClient}</span>
              </div>
            )}
            <div className="text-muted-foreground">
              Valor total: <span className="font-semibold tabular-nums text-foreground">{formatCurrency(selectedTotal)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={clearSelection}>
              Limpar Seleção
            </Button>
            <Button onClick={handleCreateInvoice}>
              <Plus className="size-4" />
              Criar Fatura
            </Button>
          </div>
        </div>
      )}

      {/* Items List */}
      {loading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando itens a faturar...</span>
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : Object.keys(itemsByClient).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <FileText className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhum item para faturar</h3>
            <p className="text-sm text-muted-foreground">
              Não há transações ou horas pendentes de faturamento.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {Object.entries(itemsByClient).map(([clientId, items]) => {
            const clientName = items[0]?.client_name || 'Cliente não identificado';
            const clientTotal = items.reduce((sum, item) => sum + item.total, 0);

            return (
              <Card key={clientId} className="min-w-0 gap-0 py-0">
                {/* Client Header */}
                <div className="flex flex-col gap-2 border-b border-border bg-muted/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <h2 className="break-words text-base font-semibold">{clientName}</h2>
                    <div className="text-sm text-muted-foreground">
                      {items.length} {items.length === 1 ? 'item' : 'itens'} • Total: {formatCurrency(clientTotal)}
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" className="self-start sm:self-auto" onClick={() => selectAllForClient(clientId)}>
                    Selecionar Todos
                  </Button>
                </div>

                {/* Items */}
                <div className="divide-y divide-border">
                  {items.map((item) => {
                    const isSelected = selectedItems.has(item.id);

                    return (
                      <button
                        type="button"
                        key={item.id}
                        aria-pressed={isSelected}
                        onClick={() => toggleItem(item.id)}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
                          isSelected && 'bg-accent'
                        )}
                      >
                        <span className="shrink-0 pt-0.5">
                          {isSelected ? (
                            <CheckSquare className="size-5 text-primary" aria-hidden="true" />
                          ) : (
                            <Square className="size-5 text-muted-foreground" aria-hidden="true" />
                          )}
                        </span>

                        <span className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <span className="min-w-0">
                            <span className="block break-words text-sm font-medium">
                              {item.description}
                            </span>
                            <span className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              <span>
                                Tipo: {item.type === 'transaction' ? 'Despesa' : 'Horas'}
                              </span>
                              <span>Data: {formatDate(item.date)}</span>
                              {item.case_name && (
                                <span>Processo: {item.case_name}</span>
                              )}
                              {item.type === 'timesheet' && (
                                <span>
                                  {item.quantity.toFixed(2)} h × {formatCurrency(item.unit_price)}
                                </span>
                              )}
                            </span>
                          </span>
                          <AmountText tone="neutral" className="shrink-0 text-sm font-semibold">
                            {formatCurrency(item.total)}
                          </AmountText>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Invoice Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open && !creating) setShowCreateModal(false) }}>
        <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Criar Fatura</DialogTitle>
            <DialogDescription>Defina vencimento, desconto e instruções da fatura.</DialogDescription>
          </DialogHeader>

          <form onSubmit={submitCreateInvoice} className="flex min-w-0 flex-col gap-4">
            {/* Invoice Summary */}
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="text-sm text-muted-foreground">Cliente</div>
              <div className="break-words text-base font-semibold">{selectedClient}</div>
              <div className="mt-2 text-sm text-muted-foreground">
                {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'itens'} selecionado(s)
              </div>
              <div className="mt-1 text-sm font-semibold tabular-nums">
                Subtotal: {formatCurrency(selectedTotal)}
              </div>
            </div>

            <FormField label="Data de Vencimento *" htmlFor="invoice-due-date">
              <Input
                id="invoice-due-date"
                type="date"
                required
                value={invoiceData.due_date}
                onChange={(e) => setInvoiceData({ ...invoiceData, due_date: e.target.value })}
              />
            </FormField>

            <FormField label="Desconto (R$)" htmlFor="invoice-discount">
              <Input
                id="invoice-discount"
                type="number"
                step="0.01"
                min="0"
                value={invoiceData.discount}
                onChange={(e) => setInvoiceData({ ...invoiceData, discount: parseFloat(e.target.value) || 0 })}
              />
            </FormField>

            <FormField label="Observações" htmlFor="invoice-notes">
              <Textarea
                id="invoice-notes"
                rows={3}
                value={invoiceData.notes}
                onChange={(e) => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                placeholder="Observações internas..."
              />
            </FormField>

            <FormField label="Instruções de Pagamento" htmlFor="invoice-payment-instructions">
              <Textarea
                id="invoice-payment-instructions"
                rows={3}
                value={invoiceData.payment_instructions}
                onChange={(e) => setInvoiceData({ ...invoiceData, payment_instructions: e.target.value })}
                placeholder="Instruções que aparecerão na fatura..."
              />
            </FormField>

            {/* Total Calculation */}
            <div className="flex flex-col gap-1 rounded-lg border border-border bg-muted/50 p-4">
              <div className="flex justify-between gap-4 text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <AmountText tone="neutral">{formatCurrency(selectedTotal)}</AmountText>
              </div>
              {invoiceData.discount > 0 && (
                <div className="flex justify-between gap-4 text-sm">
                  <span className="text-muted-foreground">Desconto:</span>
                  <AmountText tone="expense">- {formatCurrency(invoiceData.discount)}</AmountText>
                </div>
              )}
              <div className="mt-1 flex justify-between gap-4 border-t border-border pt-2 text-base font-semibold">
                <span>Total:</span>
                <span className="tabular-nums">{formatCurrency(selectedTotal - invoiceData.discount)}</span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} disabled={creating}>
                Cancelar
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <FileText className="size-4" />
                    Criar Fatura
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
